import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, TextField, InputLabel, Divider, Stack, Alert, Box } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import ProgressButtons from './common/Buttons/ProgressButtons';
import { AssociateFLSCard } from '../services/RegisterFamiliplusCard';
import { cardValidationRegex, phoneValidationRegex } from './common/regex';
import {
  FormatDefaultFamiliplusCardValue,
  FormatDefaultValuePhoneNumber,
  onFormatPhoneChange,
} from './common/FieldFormat';
import config from '../config';
import FamiliplusAdvantages from './fp-advantages/FamiliplusAdvantages';
import CreationCard from './CreationCard';
import useI18nForm from './common/hooks/useI18nForm';
import { ProfileInfo } from './common/props-types';
import CardInput from './common/Forms/CardInput';
import GoogleTagManager from 'common/GoogleTagManager';
import { CardCreationType, CardType } from 'services/profile/constants';

const cardStartingValue = config.CARD_STARTING_VALUE;

const RegisterFamiliplusCard = (props: {
  stepHandler: (value: number | ((prevVar: number) => number)) => void;
  currentStep: number;
  profileAuth: { sessionId: string; token: string };
  profileHandler: (value: ProfileInfo | ((prevVar: ProfileInfo) => ProfileInfo)) => void;
  profile: ProfileInfo;
  hasPrevious?: boolean;
  hasSkip?: boolean;
  customSubmitText?: string;
  customTitle?: string;
  showAdvantages?: boolean;
  isCreatingAccount: boolean;
}): JSX.Element => {
  const { t } = useTranslation('component');
  const [apiErrors, setErrors] = useState({ hasError: false, errorMessage: '' });
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const hasPhoneNumber = (props?.profile?.homePhone || '').length > 0 || (props?.profile?.mobilePhone || '').length > 0;

  //Puisqu'on affiche ce component avec les informations du profile à jour, nous souhaitons exécuter ce useEffect seulement une fois.
  //Cette modification est nécessaire au fonctionnement des testes cypress. Sinon, puisque nous ne redirigeons pas, le useEffect est appelé à l'infini.
  useEffect(() => {
    const IsAssociated = props.profile.familiplusCard != undefined && props.profile.familiplusCard.length >= 10;

    if (IsAssociated) {
      props.stepHandler(props.currentStep + 0.5);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const AssociateCardSchema = Yup.object().shape({
    CardNumber: Yup.string()
      .matches(
        cardValidationRegex,
        `${t('FormValidations.PleaseEnter')} ${t('Prepositions.A')} ${t('FormValidations.ValidCardNumber')}`
      )
      .required(`${t('FormValidations.PleaseEnter')} ${t('Prepositions.A')} ${t('FormValidations.ValidCardNumber')}`),
    AssociatedCardPhone: Yup.string()
      .matches(
        phoneValidationRegex,
        `${t('FormValidations.PleaseEnter')} ${t('Prepositions.A')} ${t('FormValidations.ValidPhoneNumber')}`
      )
      .required(`${t('FormValidations.PleaseEnter')} ${t('Prepositions.A')} ${t('FormValidations.ValidPhoneNumber')}`),
  });

  const handleSubmit = async (values: any) => {
    if (!isLoading) {
      setIsLoading(true);
      setErrors({ hasError: false, errorMessage: '' });

      try {
        const familiplusCard = cardStartingValue + values.CardNumber?.replace(/\D/g, '');
        const familiplusNumber = values.AssociatedCardPhone?.replace(/\D/g, '');

        const associateFLSCardResponse = await AssociateFLSCard(
          props.profileAuth,
          familiplusCard,
          familiplusNumber,
          props.isCreatingAccount
        );

        if (associateFLSCardResponse.ok) {
          props.profile.familiplusCard = familiplusCard;
          props.profile.familiplusNumber = familiplusNumber;

          props.profileHandler((prevProfile) => {
            return { ...prevProfile, ...props.profile };
          });

          if (props.isCreatingAccount) {
            GoogleTagManager.sendJoinFamiliplusSubmit(CardCreationType.Link, CardType.Physical);
          }

          props.stepHandler(props.currentStep + 0.5);
        } else {
          let message = 'Errors.GenericError';
          const status = associateFLSCardResponse.status;
          const body = await associateFLSCardResponse.json();
          //Avant, ça fonctionnait avec le status. Cependant, du jour au lendemain, ça ne fonctionne plus et il fait utilsier le code d'erreur.
          //Donc on garde les deux façons de faire au cas pour faire sûr que tout fonctionne.
          if (status == 401 || status == 404 || (body?.Messages && body.Messages.length == 1)) {
            if (status == 401) {
              message = 'Errors.Associate401';
            } else if (status == 404 || body.Messages[0].Code == 27007) {
              message = 'Errors.Associate404';
            } else if (body.Messages[0].Code == 27006) {
              message = 'Errors.AlreadyAssociated';
            }
          }
          setErrors({ hasError: true, errorMessage: message });
        }
      } catch (e) {
        setErrors({ hasError: true, errorMessage: 'Errors.GenericError' });
        return;
      } finally {
        setIsLoading(false);
      }
    }
  };

  const formik = useFormik({
    initialValues: {
      CardNumber: FormatDefaultFamiliplusCardValue(props.profile.familiplusCard!),
      AssociatedCardPhone: FormatDefaultValuePhoneNumber(props.profile.familiplusNumber!),
    },
    validationSchema: AssociateCardSchema,
    onSubmit: (values: any) => {
      handleSubmit(values);
    },
  });

  const beforeSkip = () => {
    if (props.isCreatingAccount) {
      GoogleTagManager.sendJoinFamiliplusSkip();
    }
  };

  useI18nForm({ errors: formik.errors, touched: formik.touched, setFieldTouched: formik.setFieldTouched });

  return (
    <Box data-cy="RegisterFamiliplusCard">
      <Typography variant="h1" sx={{ mb: '24px' }}>
        {t('RegisterFamiliplusCard.' + (props.customTitle || 'Title'))}
      </Typography>

      <form noValidate onSubmit={formik.handleSubmit} id="FormRegisterCard">
        {props.showAdvantages !== false && (
          <>
            <FamiliplusAdvantages />
            <Divider sx={{ my: '32px' }} />
          </>
        )}

        {apiErrors.hasError && (
          <Stack spacing={2}>
            <Alert severity="error" id="ErrorMessage">
              {t(apiErrors.errorMessage)}
            </Alert>
          </Stack>
        )}

        {!hasPhoneNumber && (
          <Stack spacing={2}>
            <Alert severity="warning" data-cy="WarningPhoneNumber">
              {t('RegisterFamiliplusCard.WarningPhoneNumber')}
            </Alert>
          </Stack>
        )}

        <Typography sx={{ fontWeight: 'bold', my: '32px' }}>{t('RegisterFamiliplusCard.SubTitle')}</Typography>
        <Box sx={{ mb: '16px' }}>
          <CardInput
            formik={formik}
            cardStartingValue={cardStartingValue}
            cardNumberLabel={t('RegisterFamiliplusCard.CardNumber')}></CardInput>
        </Box>
        <Box>
          <InputLabel htmlFor={'AssociatedCardPhone'} sx={{ whiteSpace: 'normal' }}>
            {t('RegisterFamiliplusCard.AssociatedCardPhone')}
          </InputLabel>
          <TextField
            placeholder={t('Form.PhoneNumber').toString()}
            required
            fullWidth
            name="AssociatedCardPhone"
            type="text"
            id="AssociatedCardPhone"
            defaultValue={formik.values.AssociatedCardPhone}
            onChange={(event) => onFormatPhoneChange(event, formik.handleChange)}
            inputProps={{ maxLength: 14 }}
            error={formik.touched.AssociatedCardPhone && Boolean(formik.errors.AssociatedCardPhone)}
            helperText={
              formik.touched.AssociatedCardPhone && formik.errors.AssociatedCardPhone
                ? formik.errors.AssociatedCardPhone.toString()
                : ''
            }
          />
        </Box>

        <Divider sx={{ my: '32px' }} />

        <CreationCard
          profileAuth={props.profileAuth}
          stepHandler={props.stepHandler}
          currentStep={props.currentStep}
          profileHandler={props.profileHandler}
          profile={props.profile}
          isLoading={isLoading}
          updatesNewsletterConsent={props.isCreatingAccount}
          disabled={!hasPhoneNumber}
          isCreatingAccount={props.isCreatingAccount}
        />

        <Divider sx={{ my: '32px' }} />

        <ProgressButtons
          stepHandler={props.stepHandler}
          currentStep={props.currentStep}
          hasPrevious={props.hasPrevious === undefined ? false : props.hasPrevious}
          hasSkip={props.hasSkip === undefined ? true : props.hasSkip}
          skipCustomEvent={beforeSkip}
          customSubmitText={props.customSubmitText || null}
          isLoading={isLoading}
          disabled={!hasPhoneNumber}
        />
      </form>
    </Box>
  );
};

export default RegisterFamiliplusCard;
